import { useMemo } from 'react';
import {
  externalSystemsForDiagram,
  resolveExternalSystemsGlossary,
} from '@/lib/auraExternalSystems';

function groupByCategory(systems) {
  const map = new Map();
  systems.forEach((sys) => {
    const cat = sys.category || 'Connected system';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(sys);
  });
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function SystemRow({ sys }) {
  return (
    <div className="rounded-lg border border-sky-500/25 bg-sky-500/5 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">{sys.name}</span>
        <span className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[10px] text-zinc-400">
          {sys.category}
        </span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{sys.description}</p>
      {sys.identificationSource ? (
        <p className="mt-1.5 border-t border-[var(--line)] pt-1.5 text-[10px] leading-relaxed text-muted-foreground/90">
          <span className="font-medium text-foreground/70">How identified: </span>
          {sys.identificationSource}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Legend for connected / satellite systems — descriptions come from the Analysis Agent.
 */
export default function AuraExternalSystemsLegend({
  inventory,
  nodes,
  title = 'Connected systems',
  description = 'Identified and described by the Analysis Agent from this readiness package — not a fixed catalog.',
  compact = false,
  diagramOnly = false,
}) {
  const systems = useMemo(() => {
    if (diagramOnly) return externalSystemsForDiagram({ inventory, nodes });
    return resolveExternalSystemsGlossary({ inventory, nodes });
  }, [inventory, nodes, diagramOnly]);

  if (!systems.length) {
    return (
      <div
        className={`rounded-xl border border-[var(--line)] bg-[var(--inset)] ${
          compact ? 'px-3 py-2' : 'px-3 py-3'
        }`}
        data-testid="external-systems-legend-empty"
      >
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {description}
          {' '}
          No named external systems were catalogued yet — re-run analysis after deploying the latest agent,
          or upload a readiness package with interface namespace evidence.
        </p>
      </div>
    );
  }

  const grouped = groupByCategory(systems);

  return (
    <div
      className={`rounded-xl border border-[var(--line)] bg-[var(--inset)] ${
        compact ? 'px-3 py-2' : 'px-3 py-3'
      }`}
      data-testid="external-systems-legend"
    >
      <div>
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        <p className="mt-1 text-[11px] text-sky-300/90">
          {systems.length} system{systems.length === 1 ? '' : 's'} identified in this
          {diagramOnly ? ' diagram' : ' project'}
        </p>
      </div>

      <div className={`mt-3 space-y-4 ${compact ? 'max-h-52 overflow-y-auto pr-1' : ''}`}>
        {grouped.map(([category, rows]) => (
          <div key={category}>
            <h5 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {category}
            </h5>
            <div className="grid gap-2 sm:grid-cols-2">
              {rows.map((sys) => (
                <SystemRow key={sys.id} sys={sys} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
